import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { ERROR_MESSAGES, GENERIC_ERROR_MESSAGE, VIOLATION_MESSAGES } from '../errors/error-codes';
import { ToastService } from 'src/app/shared/services/toast.service';

export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const toast = inject(ToastService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const body = parseErrorBody(error.error);
      const errorCode: string | undefined = body?.errorCode;
      const detail: string | undefined = body?.detail;
      const message: string | undefined = errorCode ? ERROR_MESSAGES[errorCode] : undefined;

      const isPublic = req.url.includes('pub/');
      const isSessionLost = !isPublic && (error.status === 401 || errorCode === 'AUTHENTICATION_REQUIRED');

      if (isSessionLost) {
        if (auth.isAuthenticated()) {
          auth.clearSession();
          toast.error(message || ERROR_MESSAGES['AUTHENTICATION_REQUIRED']);
        }
      } else if (req.context.get(SKIP_ERROR_TOAST)) {
        return throwError(() => error);
      } else if (errorCode === 'VALIDATION_FAILED') {
        const violationMessage = findViolationMessage(body?.violations);
        if (violationMessage) {
          toast.error(violationMessage);
        } else {
          toast.error(message!, formatViolationFields(body?.violations));
        }
      } else if (message) {
        toast.error(message);
      } else if (detail) {
        toast.error(detail);
      } else {
        toast.error(GENERIC_ERROR_MESSAGE);
      }

      return throwError(() => error);
    }),
  );
};

function parseErrorBody(body: any): any {
  if (typeof body !== 'string') return body;
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
}

function findViolationMessage(violations: { message: string }[] | undefined): string | undefined {
  const violation = (violations ?? []).find(v => VIOLATION_MESSAGES[v.message]);
  return violation ? VIOLATION_MESSAGES[violation.message] : undefined;
}

function formatViolationFields(violations: { field: string }[] | undefined): string | undefined {
  const fields = [...new Set((violations ?? []).map(v => v.field))].map(field => {
    const words = field.replace(/([A-Z])/g, ' $1').toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
  });

  return fields.length ? `Fields: ${fields.join(', ')}` : undefined;
}
